// Affichage soigné d'un rapport journalier + impression et envoi.
import React, { useState } from 'react';
import {
  Alert, Box, Button, Divider, Grid, ListItemIcon, Menu, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import {
  PrintOutlined, SendOutlined, WhatsApp, EmailOutlined, ContentCopyOutlined, IosShareOutlined,
} from '@mui/icons-material';
import { normalizeReport, reportDate, reportDateLong, reportNumber, reportText } from '../../utils/report';
import { tokens } from '../../theme';

const Section = ({ title, total, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{title}</Typography>
      {total !== undefined && <Typography sx={{ fontFamily: tokens.mono, fontWeight: 600, color: 'text.secondary' }}>Total : {total}</Typography>}
    </Box>
    {children}
  </Box>
);

const QtyTable = ({ rows, label }) => (
  <Table size="small" sx={{ border: 1, borderColor: 'divider', '& td, & th': { py: 0.6 } }}>
    <TableHead><TableRow sx={{ bgcolor: '#F7F8FA' }}><TableCell>{label}</TableCell><TableCell align="right" sx={{ width: 140 }}>Quantité</TableCell></TableRow></TableHead>
    <TableBody>
      {rows.map((r) => (
        <TableRow key={r.k}>
          <TableCell sx={{ fontWeight: 500 }}>{r.k}{r.sub && <Typography component="span" sx={{ ml: 1, fontSize: 12.5, color: 'text.secondary' }}>{r.sub}</Typography>}</TableCell>
          <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{r.v}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const Kpi = ({ label, value }) => (
  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, px: 2, py: 1.25 }}>
    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontFamily: tokens.mono, fontSize: 24, fontWeight: 600 }}>{value}</Typography>
  </Box>
);

/** Contenu du rapport. `actions` : affiche les boutons Imprimer / Envoyer. */
export const ReportBody = ({ report }) => {
  const n = normalizeReport(report);
  const vide = !n.production.length && !n.armatures.length && !n.materiaux.length && !n.personnel.length;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 600, textTransform: 'capitalize' }}>{reportDateLong(n.date)}</Typography>
          <Typography color="text.secondary">Rédigé par <b>{n.auteur}</b>{n.saisiPar && n.saisiPar !== n.auteur ? ` · saisi par ${n.saisiPar}` : ''}</Typography>
          {report.modifie_par && (
            <Typography sx={{ fontSize: 13, color: 'warning.main' }}>
              Modifié par {report.modifie_par} le {new Date(report.updated_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontFamily: tokens.mono, color: 'text.secondary' }}>{reportNumber(n)}</Typography>
      </Box>
      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={4}><Kpi label="PBA produits" value={n.totalPba} /></Grid>
        <Grid item xs={4}><Kpi label="Armatures façonnées" value={n.totalArmatures} /></Grid>
        <Grid item xs={4}><Kpi label="Personnel mobilisé" value={n.totalPersonnel} /></Grid>
      </Grid>
      {vide && <Alert severity="info" sx={{ mb: 2 }}>Rapport sans production ni consommation déclarée.</Alert>}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          {n.production.length > 0 && (
            <Section title="Production de PBA" total={n.totalPba}>
              <QtyTable label="Type" rows={n.production.map((p) => ({ k: p.code, v: p.quantite, sub: [p.ref && `réf. ${p.ref}`, p.numeros && `n° ${p.numeros}`].filter(Boolean).join(' · ') }))} />
            </Section>
          )}
          {n.armatures.length > 0 && (
            <Section title="Armatures façonnées" total={n.totalArmatures}>
              <QtyTable label="Type" rows={n.armatures.map((a) => ({ k: a.code, v: a.quantite }))} />
            </Section>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {n.materiaux.length > 0 && (
            <Section title="Matériaux utilisés">
              <QtyTable label="Matériau" rows={n.materiaux.map((m) => ({ k: m.nom, v: m.quantite }))} />
            </Section>
          )}
          {n.personnel.length > 0 && (
            <Section title="Personnel mobilisé" total={n.totalPersonnel}>
              <QtyTable label="Fonction" rows={n.personnel.map((p) => ({ k: p.fonction, v: p.quantite }))} />
            </Section>
          )}
        </Grid>
      </Grid>
      {n.observations && (
        <Section title="Observations">
          <Box sx={{ borderLeft: 3, borderColor: tokens.amber, bgcolor: '#FBF7EE', px: 2, py: 1.25, whiteSpace: 'pre-line' }}>{n.observations}</Box>
        </Section>
      )}
    </Box>
  );
};

/** Boutons Imprimer / Envoyer (WhatsApp, e-mail, copier, partager). */
export const ReportActions = ({ report, onInfo }) => {
  const [anchor, setAnchor] = useState(null);
  const n = normalizeReport(report);
  const subject = `Rapport de production DOCC du ${reportDate(n.date)}`;
  const close = () => setAnchor(null);
  const send = {
    whatsapp: () => window.open(`https://wa.me/?text=${encodeURIComponent(reportText(report))}`, '_blank', 'noopener'),
    email: () => { window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(reportText(report, { bold: false }))}`; },
    copy: async () => {
      try { await navigator.clipboard.writeText(reportText(report, { bold: false })); onInfo?.('Texte du rapport copié'); } catch { onInfo?.('Copie impossible sur ce navigateur'); }
    },
    share: () => navigator.share({ title: subject, text: reportText(report, { bold: false }) }).catch(() => {}),
  };
  return (
    <>
      <Button variant="outlined" startIcon={<PrintOutlined />} onClick={() => window.open(`/rapports/${report.id}/imprimer`, '_blank')}>Imprimer / PDF</Button>
      <Button variant="contained" startIcon={<SendOutlined />} onClick={(e) => setAnchor(e.currentTarget)}>Envoyer</Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
        <MenuItem onClick={() => { close(); send.whatsapp(); }}><ListItemIcon><WhatsApp fontSize="small" sx={{ color: '#25D366' }} /></ListItemIcon>Par WhatsApp</MenuItem>
        <MenuItem onClick={() => { close(); send.email(); }}><ListItemIcon><EmailOutlined fontSize="small" /></ListItemIcon>Par e-mail</MenuItem>
        {typeof navigator !== 'undefined' && navigator.share && (
          <MenuItem onClick={() => { close(); send.share(); }}><ListItemIcon><IosShareOutlined fontSize="small" /></ListItemIcon>Partager…</MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { close(); send.copy(); }}><ListItemIcon><ContentCopyOutlined fontSize="small" /></ListItemIcon>Copier le texte</MenuItem>
      </Menu>
    </>
  );
};
